import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { BbTableService } from '../../../services/bb-table.service';
import { debounceTime, Subject, Subscription } from 'rxjs';

interface BasicList {
	label: string;
	value: number;
}

@Component({
	selector: 'bb-table-pager',
	templateUrl: './bb-table-pager.component.html',
	styleUrls: ['./bb-table-pager.component.scss']
})

export class BbTablePagerComponent implements OnInit, OnChanges, OnDestroy {
	@Input() total: number;
	@Input() theme: string;
	@Output() pageNumber: EventEmitter<number>;

	offsetChangedObserver: Subscription;
	totalChangedObserver: Subscription;
	limitChangedObserver: Subscription;
	loadingObserver: Subscription;
	rowLimitChangedObserver: Subscription;
	limitChanged: Subject<void>;

	subscriptions: Subscription;

	offset = 0;
	limit: number;
	limitOptions: BasicList[] = [
		{ label: '5', value: 5 },
		{ label: '10', value: 10 },
		{ label: '15', value: 15 }, // default
		{ label: '20', value: 20 },
		{ label: '25', value: 25 },
		{ label: '50', value: 50 },
		{ label: '100', value: 100 }
	];
	page = 1;
	pages: number[] = [];

	loading = false;
	toPage: number;

	constructor(private bbTableService: BbTableService) {
		this.limitChanged = new Subject<void>();
		this.subscriptions = new Subscription();
		this.pageNumber = new EventEmitter<number>();
		this.offsetChangedObserver = this.bbTableService.offsetChangedObservable.subscribe((offset: number) => {
			// Check if offset is NaN by comparing to itself. Because of NaN !== NaN.
			if (offset === offset) {
				this.offset = offset;

				if (this.limit === this.limit) {
					this.page = Math.floor(this.offset / this.limit) + 1;
					this.determinePages();
				}
			}
		});

		this.loadingObserver = this.bbTableService.loadingObservable.subscribe((loading: boolean) => this.loading = loading);

		this.limit = this.bbTableService.getLimit();
		if (!this.total) {
			this.total = this.bbTableService.getTotalAmount();
		}
		this.determinePages();

		this.limitChangedObserver = this.bbTableService.limitChangedObservable.subscribe((limit: number) => {
			if (limit === limit) {
				this.limit = limit;

				if (this.offset === this.offset) {
					this.page = Math.floor(this.offset / this.limit) + 1;
					this.determinePages();
				}
			}
		});

		this.subscriptions.add(this.limitChanged
			.pipe(debounceTime(400))
			.subscribe(() => {
				this.limit = Math.min(100, this.limit);
				this.bbTableService.setLimit(this.limit);
				this.bbTableService.storeConfig();
			})
		);
	}

	ngOnInit() {
		if (!this.limit) {
			this.limit = this.bbTableService.getLimit();
		}

		if (this.total === undefined) {
			this.totalChangedObserver = this.bbTableService.totalChangedObservable.subscribe((total: number) => {
				if (total === total) {
					this.total = total;
					this.determinePages();
				}
			});
		}

		this.page = this.bbTableService.getCurrentPage();
		this.determinePages();
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.total) {
			this.total = simpleChanges.total.currentValue;
			this.page = 1;
			this.determinePages();
		}
	}

	changePage(page: number) {
		this.toPage = null;
		this.bbTableService.setPage(page);
		this.pageNumber.emit(page);
	}

	determinePages() {
		if (typeof this.total === 'undefined' || typeof this.page === 'undefined') {
			this.pages = [];
			return;
		}

		// odd number! amount of pages at the start, end, and "around" (and including) the current page
		const bandwidth = 5;

		const totalAmountOfPages = Math.max(1, Math.ceil(this.total / this.limit));

		// first page
		this.pages = [1];

		// pages around current page
		const difference = (this.theme === 'socials-theme' ? 3 : 1);

		let around_start = Math.max(1, this.page - ((bandwidth - difference) / 2));
		let around_end = Math.min(totalAmountOfPages, this.page + ((bandwidth - difference) / 2));

		// if we are at the beginning or end, we can use a little extra before or after the current page
		const around_size = (around_end - around_start + 1);

		if (around_end === totalAmountOfPages) {
			around_start -= (bandwidth - (around_size + (this.theme === 'socials-theme' ? 0 : 1)));
			around_start = Math.max(1, around_start); // make sure we are not overstepping bounds
		} else if (around_start === 1) {
			around_end += (bandwidth - (around_size + (this.theme === 'socials-theme' ? 0 : 1)));
			around_end = Math.min(totalAmountOfPages, around_end); // make sure we are not overstepping bounds
		}

		if (this.theme === 'socials-theme') {
			if (around_end === totalAmountOfPages - 1) {
				around_start = this.page - 2;
			}
			if (around_start === 2) {
				around_end = this.page + 2;
			}
		}

		for (let i = around_start; i <= around_end; i++) {
			if (this.pages.indexOf(i) === -1) {
				this.pages.push(i);
			}
		}

		// last page
		if (this.pages.indexOf(totalAmountOfPages) === -1) {
			this.pages.push(totalAmountOfPages);
		}
	}

	ngOnDestroy() {
		if (this.offsetChangedObserver) {
			this.offsetChangedObserver.unsubscribe();
		}
		if (this.totalChangedObserver) {
			this.totalChangedObserver.unsubscribe();
		}
		if (this.limitChangedObserver) {
			this.limitChangedObserver.unsubscribe();
		}
		if (this.loadingObserver) {
			this.loadingObserver.unsubscribe();
		}

		this.subscriptions.unsubscribe();
	}
}
